import React, { useState, useEffect } from 'react';
import { Check, Zap, Loader, DollarSign, Clock, Shield, Users, TrendingUp, Camera, Mic, Cloud, FileText, ChevronDown } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { createCheckoutSession, getSubscriptionStatus } from './utils/subscriptionClient';
import { Link } from 'react-router-dom';

function Pricing() {
  const { user, isSignedIn } = useUser();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  // Check subscription status on mount if user is signed in
  useEffect(() => {
    if (isSignedIn && user?.id) {
      loadSubscriptionStatus();

      // Auto-trigger checkout if user just signed in to subscribe
      const params = new URLSearchParams(window.location.search);
      if (params.get('subscribe') === 'true') {
        window.history.replaceState({}, '', '/pricing');
        handleSubscribe();
      }
    }
  }, [isSignedIn, user?.id]);

  const loadSubscriptionStatus = async () => {
    setCheckingStatus(true);
    try {
      const status = await getSubscriptionStatus(user.id);
      setSubscriptionStatus(status);
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isSignedIn) {
      window.location.href = '/sign-in?redirect_url=' + encodeURIComponent('/pricing?subscribe=true');
      return;
    }

    setLoading(true);
    try {
      await createCheckoutSession(user);
      // User will be redirected to Stripe checkout
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert(`Failed to start checkout: ${error.message}`);
      setLoading(false);
    }
  };

  const isSubscribed = subscriptionStatus?.subscribed === true || subscriptionStatus?.status === 'active';

  const faqs = [
    {
      question: "Can I cancel anytime?",
      answer: "Yes! You can cancel your subscription at any time. If you cancel, you'll keep access to Pro features until the end of your billing period. No refunds for partial months."
    },
    {
      question: "What happens after the 7-day free trial?",
      answer: "After your 7-day free trial ends, you'll be charged $29/month. You can cancel anytime during the trial period without being charged."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe."
    },
    {
      question: "Can I change plans later?",
      answer: "Yes, you can upgrade from Free to Pro at any time. Contact support if you need to downgrade."
    },
    {
      question: "Do you offer refunds?",
      answer: "We don't offer refunds for partial months. However, you can cancel anytime and retain access until the end of your billing period."
    },
    {
      question: "Is my data secure?",
      answer: "Yes! All data is encrypted and stored securely in our cloud infrastructure. We never share your information with third parties."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/Housecalclogo.png" alt="Estimate Assistant" className="h-10" />
            <span className="text-xl font-bold text-gray-900">Estimate Assistant</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
             {isSignedIn ? (
              <Link to="/dashboard" className="bg-[#1e3a5f] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#152a45] transition">
                Dashboard
              </Link>
             ) : (
              <Link to="/sign-in" className="bg-[#1e3a5f] text-white px-4 py-2 rounded-lg font-semibold hover:bg-[#152a45] transition">
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="inline-block px-4 py-2 bg-[#1e3a5f]/10 rounded-full text-[#1e3a5f] font-semibold text-sm mb-6">
            Simple, Transparent Pricing
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Start with our free plan and upgrade when you're ready. All plans include secure cloud storage and professional PDF exports.
          </p>
          <div className="flex items-center justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Shield className="text-[#1e3a5f]" size={20} />
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="text-[#1e3a5f]" size={20} />
              <span>Cancel Anytime</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="text-[#1e3a5f]" size={20} />
              <span>7-Day Free Trial</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Cards Section */}
      <div className="max-w-7xl mx-auto px-6 py-16 -mt-10">

        {/* Subscription Status Banner */}
        {isSignedIn && !checkingStatus && (
          <div className="max-w-4xl mx-auto mb-8">
            {isSubscribed ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-emerald-700">
                  <Check size={20} />
                  <span className="font-semibold">
                    You have an active subscription! Status: {subscriptionStatus?.status}
                  </span>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
                <span className="text-blue-700 font-medium">
                  You're currently on the free plan. Upgrade to unlock unlimited features!
                </span>
              </div>
            )}
          </div>
        )}

        {checkingStatus && (
          <div className="max-w-4xl mx-auto mb-8 text-center">
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <Loader className="animate-spin" size={20} />
              <span>Checking subscription status...</span>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Free Plan */}
          <div className="bg-white rounded-2xl shadow-lg border-2 border-gray-200 p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Free</h3>
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-5xl font-bold text-gray-900">$0</span>
                <span className="text-gray-500">/month</span>
              </div>
              <p className="text-gray-600">Perfect for trying out the tool</p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">3 estimates per month</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Basic material pricing</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">Manual input</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-gray-400 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700">PDF export</span>
              </li>
            </ul>

            <button
              disabled
              className="w-full bg-gray-200 text-gray-500 py-4 rounded-lg font-semibold cursor-not-allowed"
            >
              Current Plan
            </button>
          </div>

          {/* Pro Plan */}
          <div className="bg-gradient-to-br from-[#1e3a5f] to-[#152a45] rounded-2xl shadow-2xl border-2 border-[#1e3a5f] p-8 relative transform hover:scale-105 transition-all">
            {/* 7-Day Trial Badge */}
            <div className="absolute -top-3 -right-3">
              <div className="bg-emerald-500 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg">
                7-DAY FREE TRIAL
              </div>
            </div>

            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
              <div className="flex items-baseline justify-center gap-2 mb-4">
                <span className="text-5xl font-bold text-white">$29</span>
                <span className="text-slate-300">/month</span>
              </div>
              <p className="text-slate-200">For professional contractors</p>
            </div>

            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Unlimited estimates</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">AI-powered materials detection</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Photo upload & analysis</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Voice input</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Regional pricing data</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Cloud storage & sync</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Professional PDF templates</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-slate-300 flex-shrink-0 mt-1" size={20} />
                <span className="text-white font-medium">Priority support</span>
              </li>
            </ul>

            <button
              onClick={handleSubscribe}
              disabled={loading || isSubscribed}
              className={`w-full py-4 rounded-lg font-bold text-lg transition-all flex items-center justify-center gap-2 ${
                isSubscribed
                  ? 'bg-white/20 text-white/50 cursor-not-allowed'
                  : loading
                  ? 'bg-white/90 text-[#1e3a5f] cursor-wait'
                  : 'bg-white text-[#1e3a5f] hover:bg-slate-50 hover:scale-105 shadow-lg'
              }`}
            >
              {loading ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  <span>Loading...</span>
                </>
              ) : isSubscribed ? (
                <>
                  <Check size={20} />
                  <span>Active Subscription</span>
                </>
              ) : (
                <>
                  <Zap size={20} />
                  <span>Upgrade to Pro</span>
                </>
              )}
            </button>

            {!isSignedIn && (
              <p className="text-center text-slate-300 text-sm mt-4">
                Sign in required to subscribe
              </p>
            )}
          </div>
        </div>

        {/* Cancellation Policy */}
        <div className="mt-12 max-w-4xl mx-auto bg-slate-50 border border-slate-200 rounded-xl p-6 text-center">
          <p className="text-slate-700 leading-relaxed">
            <strong>Cancel anytime.</strong> If you cancel, you'll keep access to Pro features until the end of your billing period. No refunds for partial months.
          </p>
        </div>
      </div>

      {/* Feature Icons Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">Powerful features to streamline your estimating workflow</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="inline-flex p-4 bg-[#1e3a5f]/10 rounded-full mb-4">
                <Camera className="text-[#1e3a5f]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Photo Analysis</h3>
              <p className="text-gray-600">Upload project photos and let AI identify materials automatically</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="inline-flex p-4 bg-[#1e3a5f]/10 rounded-full mb-4">
                <Mic className="text-[#1e3a5f]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Voice Input</h3>
              <p className="text-gray-600">Speak your project details and watch them convert to estimates</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="inline-flex p-4 bg-[#1e3a5f]/10 rounded-full mb-4">
                <Cloud className="text-[#1e3a5f]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Cloud Sync</h3>
              <p className="text-gray-600">Access your estimates from anywhere with automatic cloud backup</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="inline-flex p-4 bg-[#1e3a5f]/10 rounded-full mb-4">
                <FileText className="text-[#1e3a5f]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Professional PDFs</h3>
              <p className="text-gray-600">Export polished, client-ready estimates in seconds</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="inline-flex p-4 bg-[#1e3a5f]/10 rounded-full mb-4">
                <DollarSign className="text-[#1e3a5f]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Smart Pricing</h3>
              <p className="text-gray-600">Regional pricing data keeps your estimates accurate and competitive</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="inline-flex p-4 bg-[#1e3a5f]/10 rounded-full mb-4">
                <TrendingUp className="text-[#1e3a5f]" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Time Savings</h3>
              <p className="text-gray-600">Create estimates in minutes instead of hours with AI assistance</p>
            </div>
          </div>
        </div>
      </div>

      
      {/* FAQ Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Everything you need to know</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold text-gray-900">{faq.question}</span>
                  <ChevronDown
                    className={`text-gray-500 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                    size={20}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-[#1e3a5f] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Estimating?
          </h2>
          <p className="text-xl text-slate-200 mb-8">
            Join thousands of contractors who are saving time and winning more jobs with professional estimates.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/sign-up"
              className="bg-white text-[#1e3a5f] px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-100 transition shadow-lg"
            >
              Start Free Trial
            </Link>
            <Link
              to="/"
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/10 transition"
            >
              Learn More
            </Link>
          </div>
          <p className="text-slate-300 text-sm mt-6">
            No credit card required • 7-day free trial • Cancel anytime
          </p>
        </div>
      </div>
    </div>
  );
}

export default Pricing;
